import React, { useState, useEffect, useCallback } from 'react';
import { getFreestylers, createFreestyler, checkGrammar, getRegistrations } from '../services/api';
import { logError } from '../lib/logError';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { User, Instagram, Music2, Plus, X, Upload, Loader2, Check, MapPin, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import PrivacyCheckbox from '../components/PrivacyCheckbox';

const FreestylerPage = () => {
  const [freestylers, setFreestylers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkingGrammar, setCheckingGrammar] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [selectedFreestyler, setSelectedFreestyler] = useState(null);
  const [formData, setFormData] = useState({
    name: '', nickname: '', bio: '', luogo_crew: '', instagram: '', tiktok: '', image: null
  });

  const loadFreestylers = useCallback(async () => {
    try {
      const data = await getFreestylers();
      setFreestylers(data);
    } catch (e) {
      logError('[freestyler] caricamento profili fallito', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRegistrations = useCallback(async () => {
    try {
      const data = await getRegistrations();
      setRegistrations(data);
    } catch (e) {
      logError('[freestyler] caricamento iscrizioni fallito', e);
    }
  }, []);

  useEffect(() => {
    loadFreestylers();
    loadRegistrations();
  }, [loadFreestylers, loadRegistrations]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData(p => ({ ...p, image: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleGrammarCheck = useCallback(async () => {
    if (!formData.bio || formData.bio.length < 10) {
      toast({ title: "Attenzione", description: "Scrivi almeno 10 caratteri nella bio per la correzione", variant: "destructive" });
      return;
    }
    setCheckingGrammar(true);
    try {
      const result = await checkGrammar(formData.bio);
      if (result.changed) {
        setFormData(p => ({ ...p, bio: result.corrected }));
        toast({ title: "Correzione completata", description: "La bio è stata corretta grammaticalmente." });
      } else {
        toast({ title: "Nessuna correzione necessaria", description: "La tua bio è già corretta!" });
      }
    } catch {
      toast({ title: "Errore", description: "Impossibile verificare la grammatica", variant: "destructive" });
    } finally {
      setCheckingGrammar(false);
    }
  }, [formData.bio]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.nickname || !formData.bio) {
      toast({ title: "Errore", description: "Compila nome, nickname e bio", variant: "destructive" });
      return;
    }
    if (!privacyAccepted) {
      toast({ title: "Errore", description: "Devi accettare l'informativa sulla privacy per procedere", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      // Auto grammar check before submitting
      let finalBio = formData.bio;
      try {
        const grammarResult = await checkGrammar(formData.bio);
        if (grammarResult.changed) {
          finalBio = grammarResult.corrected;
        }
      } catch (e) {
        logError('[freestyler] correzione grammaticale non disponibile, uso la bio originale', e);
      }

      await createFreestyler({
        name: formData.name,
        nickname: formData.nickname,
        bio: finalBio,
        luogo_crew: formData.luogo_crew.slice(0, 30),
        image: formData.image,
        instagram: formData.instagram,
        tiktok: formData.tiktok,
      });
      toast({
        title: "Profilo inviato!",
        description: "Il tuo profilo è stato inviato per approvazione. Riceverai conferma via email."
      });
      setShowForm(false);
      setFormData({ name: '', nickname: '', bio: '', luogo_crew: '', instagram: '', tiktok: '', image: null });
      setPrivacyAccepted(false);
    } catch {
      toast({ title: "Errore", description: "Impossibile inviare il profilo. Riprova.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-10 sm:py-16 px-4" style={{ backgroundColor: 'var(--tp-black)', minHeight: '80vh' }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white"
            style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>
            FREESTYLER ISCRITTI
          </h1>
          <Button onClick={() => setShowForm(!showForm)}
            className="bg-red-600 hover:bg-red-700 text-white gap-2 text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {showForm ? <X size={16} /> : <Plus size={16} />}
            {showForm ? 'CHIUDI' : 'ISCRIVITI'}
          </Button>
        </div>
        <div className="w-20 h-1 mb-3" style={{ backgroundColor: 'var(--tp-red)' }} />
        <p className="text-gray-400 text-sm mb-8">
          Elenco dei freestyler iscritti al torneo. Clicca su un nome per visualizzare il profilo completo.
        </p>

        {/* Registration Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="mb-10 p-5 sm:p-8 rounded-lg border border-gray-800"
            style={{ backgroundColor: 'var(--tp-dark)' }}>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-6" style={{ fontFamily: 'Oswald, sans-serif' }}>
              CREA IL TUO PROFILO FREESTYLER
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>NOME *</label>
                  <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    className="bg-gray-900 border-gray-700 text-white focus:border-red-500" placeholder="Il tuo nome" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>NICKNAME *</label>
                  <Input value={formData.nickname} onChange={e => setFormData(p => ({ ...p, nickname: e.target.value }))}
                    className="bg-gray-900 border-gray-700 text-white focus:border-red-500" placeholder="Il tuo nome d'arte" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    LUOGO E CREW DI PROVENIENZA
                    <span className="text-gray-600 ml-2 font-normal">({formData.luogo_crew.length}/30)</span>
                  </label>
                  <Input
                    value={formData.luogo_crew}
                    onChange={e => {
                      if (e.target.value.length <= 30) setFormData(p => ({ ...p, luogo_crew: e.target.value }));
                    }}
                    className="bg-gray-900 border-gray-700 text-white focus:border-red-500"
                    placeholder="Es: Milano / Cypher Kings"
                    maxLength={30}
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>INSTAGRAM</label>
                  <Input value={formData.instagram} onChange={e => setFormData(p => ({ ...p, instagram: e.target.value }))}
                    className="bg-gray-900 border-gray-700 text-white focus:border-red-500" placeholder="https://instagram.com/..." />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>TIKTOK</label>
                  <Input value={formData.tiktok} onChange={e => setFormData(p => ({ ...p, tiktok: e.target.value }))}
                    className="bg-gray-900 border-gray-700 text-white focus:border-red-500" placeholder="https://tiktok.com/@..." />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-gray-400 text-xs tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>BIO *</label>
                    <button type="button" onClick={handleGrammarCheck} disabled={checkingGrammar}
                      className="flex items-center gap-1 text-[10px] sm:text-xs text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                      style={{ fontFamily: 'Oswald, sans-serif' }}>
                      {checkingGrammar ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      CORREGGI GRAMMATICA
                    </button>
                  </div>
                  <Textarea value={formData.bio} onChange={e => setFormData(p => ({ ...p, bio: e.target.value }))}
                    className="bg-gray-900 border-gray-700 text-white focus:border-red-500 min-h-[140px]"
                    placeholder="Racconta chi sei, la tua esperienza nel freestyle..." />
                  <p className="text-gray-600 text-[10px] mt-1">La grammatica verrà corretta automaticamente all'invio</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-2 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>FOTO PROFILO</label>
                  <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-red-500 transition-colors">
                    {formData.image ? (
                      <img src={formData.image} alt="Preview" className="w-20 h-20 rounded-full object-cover mb-2" />
                    ) : (
                      <Upload size={28} className="text-gray-500 mb-2" />
                    )}
                    <span className="text-gray-500 text-sm">{formData.image ? 'Cambia foto' : 'Carica la tua foto'}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <PrivacyCheckbox checked={privacyAccepted} onCheckedChange={setPrivacyAccepted} formType="profilo" />
            </div>
            <Button type="submit" disabled={submitting} className="mt-5 w-full sm:w-auto px-8 py-3 text-white font-bold"
              style={{ backgroundColor: 'var(--tp-red)', fontFamily: 'Oswald, sans-serif' }}>
              {submitting ? <><Loader2 size={16} className="animate-spin mr-2" />INVIO IN CORSO...</> : 'INVIA PER APPROVAZIONE'}
            </Button>
            <p className="text-gray-500 text-xs mt-3">
              Il tuo profilo sarà visibile dopo l'approvazione da parte dello staff.
            </p>
          </form>
        )}

        {/* Freestylers List — 3 columns of names */}
        {loading ? (
          <div className="text-center py-16"><Loader2 size={32} className="animate-spin text-red-500 mx-auto" /></div>
        ) : freestylers.length === 0 && registrations.length === 0 ? (
          <div className="text-center py-16 rounded-lg" style={{ backgroundColor: 'var(--tp-dark)' }}>
            <User size={40} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>Nessun freestyler iscritto</p>
            <p className="text-gray-500 text-sm mt-2">Sii il primo a iscriverti!</p>
          </div>
        ) : (
          <>
            {/* Freestyler con profili completi (approvati) */}
            {freestylers.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  <Check size={20} className="text-green-500" />
                  PROFILI APPROVATI ({freestylers.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1">
                  {freestylers.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setSelectedFreestyler(f)}
                      className="text-left px-3 py-2.5 rounded hover:bg-gray-800/60 transition-colors duration-200 group flex items-center justify-between border-b border-gray-800/50"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--tp-red)' }} />
                        <span className="text-white text-sm font-medium truncate group-hover:text-red-400 transition-colors"
                          style={{ fontFamily: 'Oswald, sans-serif' }}>
                          {f.nickname || f.name}
                        </span>
                      </div>
                      {f.luogo_crew && (
                        <span className="text-gray-600 text-[10px] flex-shrink-0 ml-2 hidden sm:inline">{f.luogo_crew}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Nickname dalle iscrizioni 2026 - rimuove duplicati */}
            {registrations.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  <User size={20} className="text-blue-500" />
                  ISCRITTI TOTALI ({(() => {
                    const uniqueNicknames = new Set();
                    registrations.forEach(reg => {
                      const nickname = (reg.nickname || reg.nome || '').trim().toLowerCase();
                      if (nickname) uniqueNicknames.add(nickname);
                    });
                    return uniqueNicknames.size;
                  })()})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1">
                  {(() => {
                    const seen = new Set();
                    return registrations.filter((reg) => {
                      const nickname = (reg.nickname || reg.nome || '').trim().toLowerCase();
                      if (!nickname || seen.has(nickname)) return false;
                      seen.add(nickname);
                      return true;
                    }).map((reg, idx) => {
                      const displayName = reg.nickname || reg.nome;
                      const location = reg.location || '';
                      // Nascondi location se è uguale al nome o se il nome è contenuto nella location
                      const showLocation = location && 
                        location.toLowerCase() !== displayName.toLowerCase() &&
                        !location.toLowerCase().includes(displayName.toLowerCase());
                      
                      return (
                        <div
                          key={reg.id || idx}
                          className="text-left px-3 py-2.5 rounded border-b border-gray-800/50"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-blue-500" />
                            <span className="text-white text-sm font-medium truncate"
                              style={{ fontFamily: 'Oswald, sans-serif' }}>
                              {displayName}
                            </span>
                          </div>
                          {showLocation && (
                            <span className="text-gray-600 text-[10px] ml-4 block mt-0.5">{location}</span>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </>
        )}

        {/* Profile count */}
        {(freestylers.length > 0 || registrations.length > 0) && (
          <div className="mt-8 text-center text-gray-500 text-xs">
            Totale: {(() => {
              const uniqueNicknames = new Set();
              registrations.forEach(reg => {
                const nickname = (reg.nickname || reg.nome || '').trim().toLowerCase();
                if (nickname) uniqueNicknames.add(nickname);
              });
              return freestylers.length + uniqueNicknames.size;
            })()} freestyler iscritti (senza duplicati)
          </div>
        )}
      </div>

      {/* Freestyler Profile Popup */}
      <Dialog open={!!selectedFreestyler} onOpenChange={(open) => { if (!open) setSelectedFreestyler(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-gray-900 border-gray-700 text-white max-w-[90vw] sm:max-w-lg">
          {selectedFreestyler && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl sm:text-2xl" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {selectedFreestyler.name}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-2">
                {selectedFreestyler.image && (
                  <img src={selectedFreestyler.image} alt={selectedFreestyler.name}
                    className="w-full h-40 sm:h-48 object-cover rounded-lg mb-4" />
                )}
                {!selectedFreestyler.image && (
                  <div className="w-full h-32 rounded-lg mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--tp-gray)' }}>
                    <User size={48} className="text-gray-600" />
                  </div>
                )}
                <p className="text-red-400 text-lg mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  "{selectedFreestyler.nickname}"
                </p>
                {selectedFreestyler.luogo_crew && (
                  <div className="flex items-center gap-1.5 text-gray-400 text-sm mb-3">
                    <MapPin size={14} className="text-red-500" />
                    {selectedFreestyler.luogo_crew}
                  </div>
                )}
                <p className="text-gray-300 leading-relaxed text-sm sm:text-base">{selectedFreestyler.bio}</p>
                <div className="flex gap-3 mt-4">
                  {selectedFreestyler.instagram && (
                    <a href={selectedFreestyler.instagram} target="_blank" rel="noopener noreferrer"
                      className="px-3 sm:px-4 py-2 bg-gray-800 hover:bg-red-600 rounded text-sm flex items-center gap-2 transition-colors">
                      <Instagram size={14} /> Instagram
                    </a>
                  )}
                  {selectedFreestyler.tiktok && (
                    <a href={selectedFreestyler.tiktok} target="_blank" rel="noopener noreferrer"
                      className="px-3 sm:px-4 py-2 bg-gray-800 hover:bg-red-600 rounded text-sm flex items-center gap-2 transition-colors">
                      <Music2 size={14} /> TikTok
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FreestylerPage;
